import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ChevronRight, AlertTriangle, ExternalLink, ArrowRight, X, Archive, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

/**
 * Discovery Panel — top of the Grant Writer Workspace.
 * Consumes the shared Institutional Discovery Engine — future AI Staff will
 * mount similar panels filtered by their scope (partners, universities, etc.).
 */
export default function DiscoveryPanel({ onImport }) {
  const [summary, setSummary] = useState(null);
  const [opps, setOpps] = useState([]);
  const [bucket, setBucket] = useState("high");
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [qualifying, setQualifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        api.get("/discovery/opportunities/summary"),
        api.get("/discovery/opportunities", { params: { limit: 60 } }),
      ]);
      setSummary(s.data);
      setOpps(o.data);
    } catch (e) { console.debug("discovery: load failed", e?.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshSources = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/discovery/refresh", {});
      toast.success(`Polled ${data.polled} source${data.polled === 1 ? "" : "s"}.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const qualifyBatch = async () => {
    setQualifying(true);
    try {
      const { data } = await api.post("/discovery/qualify-batch", { limit: 5 });
      const ok = data.qualified.filter(x => x.ok).length;
      toast.success(`Qualified ${ok} of ${data.count} opportunities.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setQualifying(false); }
  };

  const qualifyOne = async (oid) => {
    try {
      await api.post(`/discovery/opportunities/${oid}/qualify`);
      toast.success("Qualified.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const importOne = async (oid) => {
    try {
      const { data } = await api.post(`/discovery/opportunities/${oid}/import`);
      toast.success(`Imported to Pipeline: ${data.grant.name}`);
      load();
      onImport?.(data.grant.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (oid, status) => {
    try {
      await api.post(`/discovery/opportunities/${oid}/status`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!summary) return null;

  const bucketed = {
    high: opps.filter(o => o.priority_bucket === "high" && o.status === "qualified"),
    medium: opps.filter(o => o.priority_bucket === "medium" && o.status === "qualified"),
    low: opps.filter(o => o.priority_bucket === "low" && o.status === "qualified"),
    pending: opps.filter(o => o.status === "prefilter_passed"),
  };
  const displayed = bucketed[bucket] || [];

  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="discovery-panel">
      <div className="px-5 py-4 border-b border-line bg-bone/40 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
            NEW Opportunities Found · Institutional Discovery Engine
          </div>
          <div className="text-xs text-ink-soft mt-1 flex items-center gap-2 flex-wrap">
            <Badge className="bg-forest text-bone text-[10px] uppercase tracking-widest">Coverage: {summary.coverage_label}</Badge>
            {summary.fragile_count > 0 && (
              <Badge className="bg-terracotta text-bone text-[10px] uppercase tracking-widest">
                {summary.fragile_count} Fragile — verify manually
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/discovery" data-testid="open-discovery-admin" className="text-xs text-ink-soft hover:text-terracotta flex items-center gap-1">
            Manage sources <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
          </Link>
          <Button size="sm" variant="outline" onClick={qualifyBatch} disabled={qualifying || bucketed.pending.length === 0} data-testid="qualify-batch" className="border-line">
            <Play className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
            {qualifying ? "Qualifying…" : `Qualify next ${Math.min(5, bucketed.pending.length)}`}
          </Button>
          <Button size="sm" onClick={refreshSources} disabled={refreshing} data-testid="refresh-sources" className="bg-forest text-bone hover:bg-ink rounded-full">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {refreshing ? "Polling…" : "Refresh now"}
          </Button>
        </div>
      </div>

      {/* Bucket tabs */}
      <div className="px-5 pt-3 flex gap-1 border-b border-line">
        <BucketTab active={bucket === "high"} onClick={() => setBucket("high")} label="High Priority" count={bucketed.high.length} tone="terracotta" testId="bucket-high" />
        <BucketTab active={bucket === "medium"} onClick={() => setBucket("medium")} label="Medium" count={bucketed.medium.length} testId="bucket-medium" />
        <BucketTab active={bucket === "low"} onClick={() => setBucket("low")} label="Low" count={bucketed.low.length} testId="bucket-low" />
        <BucketTab active={bucket === "pending"} onClick={() => setBucket("pending")} label="Awaiting Qualification" count={bucketed.pending.length} testId="bucket-pending" />
      </div>

      <div className="p-4 max-h-[520px] overflow-y-auto">
        {displayed.length === 0 ? (
          <div className="text-sm text-ink-soft italic text-center py-8">
            {bucket === "pending"
              ? "No opportunities awaiting qualification. Try Refresh, or add sources in Manage."
              : `No ${bucket}-priority opportunities yet. Try "Qualify next N" or "Refresh now".`}
          </div>
        ) : (
          <ul className="space-y-3">
            {displayed.map(o => (
              <OpportunityCard
                key={o.id} o={o}
                expanded={expanded === o.id}
                onToggle={() => setExpanded(expanded === o.id ? null : o.id)}
                onQualify={() => qualifyOne(o.id)}
                onImport={() => importOne(o.id)}
                onSkip={() => changeStatus(o.id, "skipped")}
                onArchive={() => changeStatus(o.id, "archived")}
                isPending={o.status === "prefilter_passed"}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 py-2 border-t border-line text-[10px] text-ink-soft italic">
        {summary.coverage_disclaimer}
      </div>
    </div>
  );
}

function BucketTab({ active, onClick, label, count, tone, testId }) {
  const activeClass = tone === "terracotta"
    ? "border-terracotta text-terracotta"
    : "border-ink text-ink";
  return (
    <button
      onClick={onClick} data-testid={testId}
      className={`px-3 py-2 text-xs border-b-2 transition-colors ${active ? activeClass : "border-transparent text-ink-soft hover:text-ink"}`}
    >
      {label} <span className="ml-1 opacity-70">· {count}</span>
    </button>
  );
}

function OpportunityCard({ o, expanded, onToggle, onQualify, onImport, onSkip, onArchive, isPending }) {
  const q = o.qualification || {};
  const bucketTone = {
    high: "bg-terracotta text-bone",
    medium: "bg-bone text-ink border border-line",
    low: "bg-line text-ink-soft",
  }[o.priority_bucket] || "bg-line text-ink-soft";
  return (
    <li className="border border-line rounded-lg bg-bone/30 overflow-hidden" data-testid={`opp-${o.id}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {o.is_fragile && (
              <Badge className="bg-terracotta/20 text-terracotta border border-terracotta/40 text-[10px] uppercase tracking-widest flex items-center gap-1">
                <AlertTriangle className="h-2.5 w-2.5" strokeWidth={1.5} /> Fragile
              </Badge>
            )}
            {o.priority_bucket && (
              <Badge className={`text-[10px] uppercase tracking-widest ${bucketTone}`}>{o.priority_bucket}</Badge>
            )}
            <Badge className="bg-bone text-ink border border-line text-[10px]">{o.source_label}</Badge>
          </div>
          <div className="font-serif text-lg text-ink mt-1.5 leading-snug">{o.title}</div>
          <div className="text-xs text-ink-soft mt-1 flex items-center gap-3 flex-wrap">
            {o.funder && <span>{o.funder}</span>}
            {(o.amount_min || o.amount_max) && (
              <span>${(o.amount_min || 0).toLocaleString()}–${(o.amount_max || 0).toLocaleString()}</span>
            )}
            {(q.extracted_deadline || o.deadline) && <span>Due: {q.extracted_deadline || o.deadline}</span>}
          </div>
          {q.why_it_matches && (
            <div className="text-sm text-ink mt-2 italic">{q.why_it_matches}</div>
          )}
          {q.fit_score != null && (
            <div className="text-xs text-ink-soft mt-2 flex items-center gap-3 flex-wrap">
              <span>Fit <strong className="text-ink">{q.fit_score}</strong></span>
              <span>Effort <strong className="text-ink">{q.effort_score}</strong></span>
              <span>Probability <strong className="text-ink">{q.probability_score}</strong></span>
              {q.recommended_action && (
                <Badge className={`text-[10px] uppercase ${
                  q.recommended_action === "proceed" ? "bg-forest text-bone" :
                  q.recommended_action === "wait" ? "bg-terracotta text-bone" :
                  "bg-line text-ink-soft"
                }`}>{q.recommended_action}</Badge>
              )}
            </div>
          )}
        </div>
        <button onClick={onToggle} className="text-ink-soft hover:text-terracotta shrink-0" data-testid={`opp-toggle-${o.id}`}>
          <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
        </button>
      </div>

      {expanded && (
        <div className="px-4 py-3 border-t border-line bg-admin-surface space-y-3">
          {o.url && (
            <a href={o.url} target="_blank" rel="noopener noreferrer" className="text-xs text-terracotta hover:underline inline-flex items-center gap-1">
              Open source page <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
            </a>
          )}
          <div className="text-xs text-ink-soft whitespace-pre-wrap max-h-40 overflow-y-auto border border-line rounded p-2 bg-bone/40">
            {(o.raw_summary || "").slice(0, 1500)}
            {(o.raw_summary || "").length > 1500 ? "…" : ""}
          </div>
          {q.concerns && q.concerns.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-terracotta mb-1">Concerns (Doctrine Rules 1, 6, 9)</div>
              <ul className="text-xs text-ink space-y-1">
                {q.concerns.map((c, i) => (
                  <li key={`concern-${i}`} className="flex items-start gap-2">
                    <span className="text-terracotta mt-1">·</span>{c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {q.required_documents && q.required_documents.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Required Documents</div>
              <ul className="text-xs text-ink space-y-1">
                {q.required_documents.map((d, i) => (
                  <li key={`req-${i}`} className="flex items-start gap-2">
                    <span className="text-ink-soft mt-1">·</span>{d}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="px-4 py-2 border-t border-line bg-bone/60 flex items-center justify-end gap-2">
        {isPending ? (
          <Button size="sm" onClick={onQualify} data-testid={`qualify-${o.id}`} className="bg-forest text-bone hover:bg-ink rounded-full h-7 text-xs">
            <Play className="h-3 w-3 mr-1" strokeWidth={1.5} /> Analyze
          </Button>
        ) : (
          <>
            <Button size="sm" onClick={onImport} data-testid={`import-${o.id}`} className="bg-forest text-bone hover:bg-ink rounded-full h-7 text-xs">
              <ArrowRight className="h-3 w-3 mr-1" strokeWidth={1.5} /> Import to Pipeline
            </Button>
            <Button size="sm" variant="outline" onClick={onQualify} data-testid={`requalify-${o.id}`} className="border-line h-7 text-xs">Re-qualify</Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={onSkip} data-testid={`skip-${o.id}`} className="border-line h-7 text-xs">
          <X className="h-3 w-3 mr-1" strokeWidth={1.5} /> Skip
        </Button>
        <Button size="sm" variant="outline" onClick={onArchive} data-testid={`archive-${o.id}`} className="border-line h-7 text-xs">
          <Archive className="h-3 w-3 mr-1" strokeWidth={1.5} /> Archive
        </Button>
      </div>
    </li>
  );
}
